import { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Check, AlertCircle } from 'lucide-react';
import { theme } from '@/lib/theme';
import type { AdminService } from '@/types';

interface BookingFeeSectionProps {
  services: AdminService[];
  loading: boolean;
  onUpdateFee: (id: string, fee: number) => Promise<string | null>;
}

interface FeeState {
  value: string;
  saving: boolean;
  saved: boolean;
  error: string | null;
}

export default function BookingFeeSection({ services, loading, onUpdateFee }: BookingFeeSectionProps) {
  const [feeStates, setFeeStates] = useState<Record<string, FeeState>>({});

  const getFeeState = (service: AdminService): FeeState =>
    feeStates[service.id] ?? {
      value: String(service.booking_fee ?? 0),
      saving: false,
      saved: false,
      error: null,
    };

  const setFeeState = (id: string, patch: Partial<FeeState>) => {
    setFeeStates((prev) => ({
      ...prev,
      [id]: { ...getFeeState({ id } as AdminService), ...patch },
    }));
  };

  const handleChange = (service: AdminService, raw: string) => {
    setFeeState(service.id, { value: raw, saved: false, error: null });
  };

  const handleSave = async (service: AdminService) => {
    const state = getFeeState(service);
    const fee = parseFloat(state.value);
    if (isNaN(fee) || fee < 0) {
      setFeeState(service.id, { error: 'Valor inválido' });
      return;
    }
    setFeeState(service.id, { saving: true, error: null });
    const err = await onUpdateFee(service.id, fee);
    if (err) {
      setFeeState(service.id, { saving: false, error: err });
    } else {
      setFeeState(service.id, { saving: false, saved: true, error: null });
      setTimeout(() => setFeeState(service.id, { saved: false }), 2500);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, service: AdminService) => {
    if (e.key === 'Enter') handleSave(service);
  };

  const inputStyle = {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderColor: theme.colors.border,
    color: '#fff',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-xl border mb-6"
      style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
    >
      {/* Header */}
      <div
        className="flex items-start gap-4 px-6 py-5"
        style={{ borderBottom: `1px solid ${theme.colors.border}` }}
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ backgroundColor: 'rgba(201,168,76,0.12)' }}
        >
          <i className="ri-money-dollar-circle-line text-base" style={{ color: theme.colors.primary }} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: theme.fonts.display }}>
            Valor de reserva (anticipo)
          </h3>
          <p className="text-xs mt-0.5 leading-relaxed" style={{ color: theme.colors.textDisabled }}>
            Monto que el cliente paga al reservar. En{' '}
            <span className="font-medium" style={{ color: theme.colors.primary }}>modo live</span>{' '}
            se cobra este valor vía Wompi. En{' '}
            <span className="font-medium text-emerald-400">modo mock</span>{' '}
            siempre se registra como{' '}
            <span className="font-semibold text-white">$0</span>{' '}
            sin importar este valor.
          </p>
        </div>
      </div>

      {/* Service rows */}
      <div className="divide-y" style={{ borderColor: theme.colors.border }}>
        {loading && [...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center justify-between px-6 py-4 gap-4">
            <div className="h-4 rounded animate-pulse w-40" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
            <div className="h-9 rounded-lg animate-pulse w-36" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
          </div>
        ))}

        {!loading && services.length === 0 && (
          <div className="px-6 py-8 text-center">
            <p className="text-sm" style={{ color: theme.colors.textDisabled }}>
              No hay servicios activos
            </p>
          </div>
        )}

        {!loading && services.map((service) => {
          const state = getFeeState(service);
          return (
            <div key={service.id} className="flex items-center gap-4 px-6 py-4">
              {/* Service info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{service.name}</p>
                <p className="text-xs mt-0.5" style={{ color: theme.colors.textDisabled }}>
                  Precio total: ${Number(service.price).toLocaleString('es-CO')} COP
                </p>
              </div>

              {/* Fee input + save */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="relative">
                  <span
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium select-none"
                    style={{ color: theme.colors.textDisabled }}
                  >
                    $
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="1000"
                    value={state.value}
                    onChange={(e) => handleChange(service, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, service)}
                    className="pl-7 pr-3 py-2 rounded-lg text-sm border outline-none transition-all w-36"
                    style={inputStyle}
                    placeholder="0"
                  />
                </div>

                <button
                  onClick={() => handleSave(service)}
                  disabled={state.saving}
                  className="h-9 px-3.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-60"
                  style={{
                    backgroundColor: state.saved
                      ? 'rgba(52,211,153,0.15)'
                      : 'rgba(201,168,76,0.15)',
                    color: state.saved ? '#34d399' : theme.colors.primary,
                    border: `1px solid ${state.saved ? 'rgba(52,211,153,0.3)' : 'rgba(201,168,76,0.3)'}`,
                  }}
                >
                  {state.saving ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : state.saved ? (
                    <Check size={12} />
                  ) : null}
                  {state.saving ? 'Guardando' : state.saved ? 'Guardado' : 'Guardar'}
                </button>
              </div>

              {/* Error / hint */}
              {state.error && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <AlertCircle size={12} className="text-red-400" />
                  <span className="text-xs text-red-400">{state.error}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <div
        className="px-6 py-3 flex items-center gap-2"
        style={{ borderTop: `1px solid ${theme.colors.border}` }}
      >
        <i className="ri-information-line text-xs" style={{ color: theme.colors.textDisabled }} />
        <p className="text-xs" style={{ color: theme.colors.textDisabled }}>
          Presiona <kbd className="px-1 py-0.5 rounded text-[10px] font-mono" style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: theme.colors.textMuted }}>Enter</kbd> o el botón Guardar para aplicar el cambio.
        </p>
      </div>
    </motion.div>
  );
}
